import { StandardEvents } from '@shopify/events';

/**
 * Plays an add-to-cart button's animation only once the add has succeeded
 * (SM-750).
 *
 * Horizon's <add-to-cart-component> animates the moment the button is clicked,
 * before the server has answered, so a refused add (the variant is already at its
 * stock limit) still showed the "added" state and the fly-to-cart. This sets the
 * component's own opt-out (`data-puppet`) on the button as it is clicked, then
 * replays the original `handleClick` animation when the add's cart event settles
 * without an error. A refused add never animates.
 *
 * It covers every add-to-cart button — the PDP's Add to bag, the card quick adds,
 * the quick-add modal — except one that already carries `data-puppet`: the sticky
 * bar drives the real button that way and animates itself. Document-level and in
 * the capture phase on `window`: the theme's own click delegation
 * (assets/component.js) is a capture listener on `document`, so this has to run
 * before it, and the Bloomreach cards render their quick add inside a shadow root.
 */

/** A cart event this long after the click is not the click's add. */
const PENDING_WINDOW_MS = 15000;

/**
 * @typedef {HTMLElement & {handleClick(event: {target: Element}): void}} AddToCartComponent
 * @typedef {{component: AddToCartComponent, button: HTMLElement, target: Element, variantId: string, at: number}} PendingAdd
 */

/** @type {PendingAdd | null} */
let pending = null;

window.addEventListener(
  'click',
  (event) => {
    const target = event.composedPath()[0];
    if (!(target instanceof Element)) return;

    const component = /** @type {AddToCartComponent | null} */ (target.closest('add-to-cart-component'));
    if (!component) return;

    const button = component.querySelector('button');
    // Someone else (the sticky bar) already owns this button's animation.
    if (!(button instanceof HTMLElement) || button.dataset.puppet === 'true') return;

    button.dataset.puppet = 'true';
    const variant = component.closest('form')?.querySelector('input[name="id"]');
    pending = { component, button, target, variantId: variant instanceof HTMLInputElement ? idTail(variant.value) : '', at: Date.now() };
    // No cart event is coming for an add that never went out; hand the button back.
    window.setTimeout(() => release(null, button), PENDING_WINDOW_MS);
  },
  { capture: true }
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const current = pending;
  if (!current || Date.now() - current.at > PENDING_WINDOW_MS) return;
  // Another variant's add is not this click's add (a click that never submitted can leave one pending).
  const merchandiseId = idTail(event.lines?.[0]?.merchandiseId);
  if (current.variantId && merchandiseId && merchandiseId !== current.variantId) return;
  pending = null;

  event.promise
    ?.then(({ detail }) => {
      release(null, current.button);
      // A refused add leaves the button as it was; a clean one plays the full animation.
      if (!detail?.didError) play(current);
    })
    .catch(() => release(null, current.button));
});

/**
 * Hands the button back to Horizon.
 *
 * @param {PendingAdd | null} entry - The pending add to drop, if it is still the current one.
 * @param {HTMLElement} button
 */
function release(entry, button) {
  if (entry && pending === entry) pending = null;
  if (button.dataset.puppet === 'true') delete button.dataset.puppet;
}

/**
 * Replays Horizon's click animation for an add that has succeeded.
 *
 * `handleClick` first asks the quantity selector whether the add would exceed the
 * stock limit, and that check counts what is already in the cart. After the add
 * that used up the last units it would now say no and skip the animation of an add
 * that worked, so the check is answered "yes" for this one synchronous call.
 *
 * @param {PendingAdd} add
 */
function play({ component, target }) {
  const selector = /** @type {any} */ (component.closest('product-form-component'))?.refs?.quantitySelector;
  const own = selector ? Object.getOwnPropertyDescriptor(selector, 'canAddToCart') : undefined;
  if (selector) selector.canAddToCart = () => ({ canAdd: true });
  try {
    component.handleClick({ target });
  } finally {
    // It is normally a class method (nothing own to put back); restore it exactly if not.
    if (selector) {
      if (own) Object.defineProperty(selector, 'canAddToCart', own);
      else delete selector.canAddToCart;
    }
  }
}

/**
 * The variant id without any `gid://shopify/ProductVariant/` prefix: the cart event
 * may carry the global id while the form holds the plain number.
 *
 * @param {unknown} id
 * @returns {string}
 */
function idTail(id) {
  return String(id ?? '').split('/').pop() ?? '';
}
